/* eslint-disable no-nested-ternary */
import CustomField, {
  ICustomField,
  ICustomFieldApiModel,
} from '@models/CustomField.model';

export const fieldConfigurationLocationOptions = <const>[
  'productHeader',
  'productTable',
  'softwareTable',
  'miscellaneousTable',
  'studioServiceTable',
  'softwareHeader',
  'miscellaneousHeader',
  'studioServiceHeader',
  'adminProductTable',
  'adminManufacturerTable',
  'adminNotificationsTable',
  'adminStudioTable',
  'productTagTable',
  'miscellaneousTagTable',
  'serviceTagTable',
  'softwareTagTable',
];
export type FieldConfigurationLocationOptions =
  typeof fieldConfigurationLocationOptions[number];
// Equipment
export const equipmentFieldNameDictionary = {
  purchasedPrice: 'Purchased Price',
  productName: 'Product Name',
  installDate: 'Install Date',
  productCategory: 'Category',
  manufacturerName: 'Manufacturer',
  productModel: 'Model #',
  productPage: 'Page URL',
  productDescription: 'Product Description',
  installedFirmwareVersion: 'Installed Firmware Version',
  installedSoftwareVersion: 'Installed Software Version',
  purchasedFrom: 'Purchased From',
  purchasedDate: 'Purchased Date',
  serial: 'Serial #',
  tags: 'Tags',
  userName: 'Assignee',
  sla: 'SLA',
  // extendedWarrantyPurchased: 'Extended Warranty Purchased',
  // extendedWarrantyLength: 'Extended Warranty Length',
  equipmentStatusName: 'Status',
  vendor: 'Vendor',
  licenseStartDate: 'License Start Date',
  licenseEndDate: 'License End Date',
  createdAt: 'Added to Beam',
  productStatus: 'Product Lifecycle',
  productWarranty: 'Product Warranty',
  identificationStatus: 'Identification',

  // imported data
  importedModel: 'Imported Model',
  importedName: 'Imported Product Name',
  importedManufacturerName: 'Imported Manufacturer',
};
export type EquipmentFieldNameDictionary = typeof equipmentFieldNameDictionary;

export const equipmentFieldSortDictionary = {
  productName: '"product"."name"',
  manufacturerName: '"manufacturer"."name"',
  productModel: '"product"."model"',
  productPage: '"product"."pageUrl"',
  productDescription: '"product"."description"',
  installedFirmwareVersion: '"equipment"."installedFirmwareVersion"',
  installedSoftwareVersion: '"equipment"."installedSoftwareVersion"',
  serial: '"equipment"."serial"',
  tags: '"tags"."name"',
  userName: '"user"."name"',
  purchasedPrice: '"equipment"."purchasedPrice"',
  installDate: '"equipment"."installDate"',
  purchasedFrom: '"equipment"."purchasedFrom"',
  purchasedDate: '"equipment"."purchasedDate"',
  sla: '"equipment"."sla"',
  licenseStartDate: '"equipment"."licenseStartDate"',
  licenseEndDate: '"equipment"."licenseEndDate"',
  vendor: '"equipment"."vendor"',
  equipmentStatusName: '"equipmentStatus"."name"',
  createdAt: '"equipment"."createdAt"',
  // Added for types, not shown/used
  productCategory: '"category"."name"',
  productStatus: '"product"."productStatus"',
  productWarranty: '"product"."standardWarrantyLength"',
  identificationStatus: '"equipment"."identificationStatus"',

  // imported data
  importedModel: '"equipment"."importedModel"',
  importedName: '"equipment"."importedName"',
  importedManufacturerName: '"equipment"."importedManufacturerName"',
};
export const equipmentFieldConfigurationTypeOptions = <const>[
  'purchasedPrice',
  'productName',
  'manufacturerName',
  'productModel',
  'productPage',
  'productDescription',
  'installedFirmwareVersion',
  'installedSoftwareVersion',
  'purchasedFrom',
  'purchasedDate',
  'installDate',
  'serial',
  'userName',
  'sla',
  'equipmentStatusName',
  'licenseStartDate',
  'licenseEndDate',
  'vendor',
  'createdAt',
  'productCategory',
  'productStatus',
  'productWarranty',
  'identificationStatus',
  'importedModel',
  'importedName',
  'importedManufacturerName',
];
export type EquipmentFieldConfigurationTypeOptions =
  typeof equipmentFieldConfigurationTypeOptions[number];
// Software
export const softwareFieldNameDictionary = {
  key: 'License Key',
  vendor: 'Software Vendor',
  version: 'Model/Version',
  name: 'Name',
  description: 'Description',
  companyOrManufacturer: 'Company or Manufacturer',
  purchasedPrice: 'Purchased Price',
  purchasedFrom: 'Purchased From',
  purchasedDate: 'Purchased Date',
  licenseStartDate: 'License Start Date',
  licenseEndDate: 'License End Date',
  equipmentStatusName: 'Status',
  userName: 'User',
  tags: 'Tags',
};
export type SoftwareFieldNameDictionary = typeof softwareFieldNameDictionary;
export const softwareFieldSortDictionary = {
  key: '"software"."key"',
  vendor: '"software"."vendor"',
  version: '"software"."version"',
  name: '"software"."name"',
  description: '"software"."description"',
  companyOrManufacturer: '"software"."companyOrManufacturer"',
  purchasedPrice: '"software"."purchasedPrice"',
  purchasedFrom: '"software"."purchasedFrom"',
  purchasedDate: '"software"."purchasedDate"',
  licenseStartDate: '"software"."licenseStartDate"',
  licenseEndDate: '"software"."licenseEndDate"',
  equipmentStatusName: '"equipmentStatus"."name"',
  userName: '"user"."name"',
  tags: '"tags"."name"',
};
export const softwareFieldConfigurationTypeOptions = <const>[
  'key',
  'vendor',
  'version',
  'name',
  'description',
  'companyOrManufacturer',
  'purchasedPrice',
  'purchasedFrom',
  'purchasedDate',
  'licenseStartDate',
  'licenseEndDate',
  'equipmentStatusName',
  'userName',
];
export type SoftwareFieldConfigurationTypeOptions =
  typeof softwareFieldConfigurationTypeOptions[number];
// Miscellaneous
export const miscellaneousFieldNameDictionary = {
  name: 'Name',
  vendor: 'Vendor',
  installDate: 'Install Date',
  customId: 'Serial #, VIN #, Etc',
  modelNumber: 'Model #',
  description: 'Description',
  purchasedPrice: 'Purchased Price',
  purchasedFrom: 'Purchased From',
  purchasedDate: 'Purchased Date',
  equipmentStatusName: 'Status',
  userName: 'User',
  tags: 'Tags',
};
export type MiscellaneousFieldNameDictionary =
  typeof miscellaneousFieldNameDictionary;
export const miscellaneousFieldSortDictionary = {
  name: '"miscellaneous"."name"',
  vendor: '"miscellaneous"."vendor"',
  installDate: '"miscellaneous"."installDate"',
  customId: '"miscellaneous"."customId"',
  modelNumber: '"miscellaneous"."modelNumber"',
  description: '"miscellaneous"."description"',
  purchasedPrice: '"miscellaneous"."purchasedPrice"',
  purchasedFrom: '"miscellaneous"."purchasedFrom"',
  purchasedDate: '"miscellaneous"."purchasedDate"',
  equipmentStatusName: '"equipmentStatus"."name"',
  userName: '"user"."name"',
  tags: '"tags"."name"',
};

export const miscellaneousFieldConfigurationTypeOptions = <const>[
  'name',
  'vendor',
  'installDate',
  'customId',
  'modelNumber',
  'description',
  'purchasedPrice',
  'purchasedFrom',
  'purchasedDate',
  'equipmentStatusName',
  'userName',
];
export type MiscellaneousFieldConfigurationTypeOptions =
  typeof miscellaneousFieldConfigurationTypeOptions[number];
// Studio Service
export const studioServiceFieldNameDictionary = {
  name: 'Name',
  description: 'Description',
  vendor: 'Vendor',
  // isRecurring: 'Recurring',
  // subscriptionCadence: 'Subscription Cadence',
  purchasedPrice: 'Purchased Price',
  purchasedFrom: 'Purchased From',
  purchasedDate: 'Purchased Date',
  startDate: 'Start Date',
  endDate: 'End Date',
  equipmentStatusName: 'Status',
  userName: 'User',
  tags: 'Tags',
};
export type StudioServiceFieldNameDictionary =
  typeof studioServiceFieldNameDictionary;
export const studioServiceFieldSortDictionary = {
  name: '"studioService"."name"',
  description: '"studioService"."description"',
  vendor: '"studioService"."vendor"',
  purchasedPrice: '"studioService"."purchasedPrice"',
  purchasedFrom: '"studioService"."purchasedFrom"',
  purchasedDate: '"studioService"."purchasedDate"',
  startDate: '"studioService"."startDate"',
  endDate: '"studioService"."endDate"',
  equipmentStatusName: '"equipmentStatus"."name"',
  userName: '"user"."name"',
  tags: '"tags"."name"',
};
export const studioServiceFieldConfigurationTypeOptions = <const>[
  'name',
  'description',
  'vendor',
  'purchasedFrom',
  'purchasedPrice',
  'purchasedDate',
  'startDate',
  'endDate',
  'equipmentStatusName',
  'userName',
];
export type StudioServiceFieldConfigurationTypeOptions =
  typeof studioServiceFieldConfigurationTypeOptions[number];

// End
export interface IFieldConfiguration {
  id: string;
  order: number;
  fieldName?:
    | EquipmentFieldConfigurationTypeOptions
    | SoftwareFieldConfigurationTypeOptions
    | MiscellaneousFieldConfigurationTypeOptions
    | StudioServiceFieldConfigurationTypeOptions;
  customField?: ICustomField;
  name: string;
  fieldFilter: string;
  location: FieldConfigurationLocationOptions;
}

export interface IFieldConfigurationApiModel {
  id: string;
  location: FieldConfigurationLocationOptions;
  order: number;
  fieldName?:
    | EquipmentFieldConfigurationTypeOptions
    | SoftwareFieldConfigurationTypeOptions
    | MiscellaneousFieldConfigurationTypeOptions
    | StudioServiceFieldConfigurationTypeOptions;
  customField?: ICustomFieldApiModel;
}

export default class FieldConfiguration implements IFieldConfiguration {
  public readonly id: string;

  public readonly location: FieldConfigurationLocationOptions;

  public readonly order: number;

  public readonly fieldName:
    | EquipmentFieldConfigurationTypeOptions
    | SoftwareFieldConfigurationTypeOptions
    | MiscellaneousFieldConfigurationTypeOptions
    | StudioServiceFieldConfigurationTypeOptions
    | undefined;

  public readonly customField: ICustomField | undefined;

  public readonly name: string;

  public readonly fieldFilter: string;

  constructor(
    id: string,
    location: FieldConfigurationLocationOptions,
    order: number,
    name: string,
    fieldFilter: string,
    fieldName?:
      | EquipmentFieldConfigurationTypeOptions
      | SoftwareFieldConfigurationTypeOptions
      | MiscellaneousFieldConfigurationTypeOptions
      | StudioServiceFieldConfigurationTypeOptions,
    customField?: ICustomField,
  ) {
    this.id = id;
    this.location = location;
    this.order = order;
    this.fieldName = fieldName;
    this.customField = customField;
    this.name = name;
    this.fieldFilter = fieldFilter;
  }

  static fromApiModel(
    apiModel: IFieldConfigurationApiModel,
    location: FieldConfigurationLocationOptions,
  ): IFieldConfiguration {
    const { id, order } = apiModel;
    if (
      location === 'productTable' ||
      location === 'productHeader' ||
      location === 'productTagTable'
    ) {
      const customField = apiModel.customField
        ? CustomField.fromApiModel(apiModel.customField)
        : undefined;
      const fieldName = apiModel.fieldName ? apiModel.fieldName : undefined;
      const name = apiModel.fieldName
        ? equipmentFieldNameDictionary[
            apiModel.fieldName as EquipmentFieldConfigurationTypeOptions
          ]
        : customField
        ? customField.name
        : '';
      const fieldFilter = apiModel.fieldName
        ? equipmentFieldSortDictionary[
            apiModel.fieldName as EquipmentFieldConfigurationTypeOptions
          ]
        : 'fields.content';
      return new FieldConfiguration(
        id,
        location,
        order,
        name,
        fieldFilter,
        fieldName,
        customField,
      );
    }
    if (
      location === 'softwareTable' ||
      location === 'softwareHeader' ||
      location === 'softwareTagTable'
    ) {
      const customField = apiModel.customField
        ? CustomField.fromApiModel(apiModel.customField)
        : undefined;
      const fieldName = apiModel.fieldName ? apiModel.fieldName : undefined;
      const name = apiModel.fieldName
        ? softwareFieldNameDictionary[
            apiModel.fieldName as SoftwareFieldConfigurationTypeOptions
          ]
        : customField
        ? customField.name
        : '';
      const fieldFilter = apiModel.fieldName
        ? softwareFieldSortDictionary[
            apiModel.fieldName as SoftwareFieldConfigurationTypeOptions
          ]
        : 'fields.content';
      return new FieldConfiguration(
        id,
        location,
        order,
        name,
        fieldFilter,
        fieldName,
        customField,
      );
    }
    if (
      location === 'miscellaneousTable' ||
      location === 'miscellaneousHeader' ||
      location === 'miscellaneousTagTable'
    ) {
      const customField = apiModel.customField
        ? CustomField.fromApiModel(apiModel.customField)
        : undefined;
      const fieldName = apiModel.fieldName ? apiModel.fieldName : undefined;
      const name = apiModel.fieldName
        ? miscellaneousFieldNameDictionary[
            apiModel.fieldName as MiscellaneousFieldConfigurationTypeOptions
          ]
        : customField
        ? customField.name
        : '';
      const fieldFilter = apiModel.fieldName
        ? miscellaneousFieldSortDictionary[
            apiModel.fieldName as MiscellaneousFieldConfigurationTypeOptions
          ]
        : 'fields.content';
      return new FieldConfiguration(
        id,
        location,
        order,
        name,
        fieldFilter,
        fieldName,
        customField,
      );
    }
    if (
      location === 'studioServiceTable' ||
      location === 'studioServiceHeader' ||
      location === 'serviceTagTable'
    ) {
      const customField = apiModel.customField
        ? CustomField.fromApiModel(apiModel.customField)
        : undefined;
      const fieldName = apiModel.fieldName ? apiModel.fieldName : undefined;
      const name = apiModel.fieldName
        ? studioServiceFieldNameDictionary[
            apiModel.fieldName as StudioServiceFieldConfigurationTypeOptions
          ]
        : customField
        ? customField.name
        : '';
      const fieldFilter = apiModel.fieldName
        ? studioServiceFieldSortDictionary[
            apiModel.fieldName as StudioServiceFieldConfigurationTypeOptions
          ]
        : 'fields.content';
      return new FieldConfiguration(
        id,
        location,
        order,
        name,
        fieldFilter,
        fieldName,
        customField,
      );
    }
    // eslint-disable-next-line no-throw-literal
    throw 'Error';
  }

  static arrayFromApiModel(
    arrayApiModel: IFieldConfigurationApiModel[],
    location: FieldConfigurationLocationOptions,
  ) {
    if (arrayApiModel.length === 0) {
      const array = FieldConfiguration.defaultArray(location);
      return array;
    }
    const array = arrayApiModel.map(apiModel =>
      FieldConfiguration.fromApiModel(apiModel, location),
    );

    return array;
  }

  static defaultArray(
    location: FieldConfigurationLocationOptions,
  ): IFieldConfiguration[] {
    if (location === 'productTable')
      return [
        {
          id: '0',
          location: 'productTable',
          order: 0,
          fieldName: 'productName',
          name: equipmentFieldNameDictionary.productName,
          fieldFilter: equipmentFieldSortDictionary.productName,
        },
        {
          id: '1',
          location: 'productTable',
          order: 1,
          fieldName: 'manufacturerName',
          name: equipmentFieldNameDictionary.manufacturerName,
          fieldFilter: equipmentFieldSortDictionary.manufacturerName,
        },
        {
          id: '2',
          location: 'productTable',
          order: 2,
          fieldName: 'productModel',
          name: equipmentFieldNameDictionary.productModel,
          fieldFilter: equipmentFieldSortDictionary.productModel,
        },
        {
          id: '3',
          location: 'productTable',
          order: 3,
          fieldName: 'serial',
          name: equipmentFieldNameDictionary.serial,
          fieldFilter: equipmentFieldSortDictionary.serial,
        },
        {
          id: '4',
          location: 'productTable',
          order: 4,
          fieldName: 'userName',
          name: equipmentFieldNameDictionary.userName,
          fieldFilter: equipmentFieldSortDictionary.userName,
        },
        {
          id: '5',
          location: 'productTable',
          order: 5,
          fieldName: 'sla',
          name: equipmentFieldNameDictionary.sla,
          fieldFilter: equipmentFieldSortDictionary.sla,
        },
      ];
    if (location === 'productHeader')
      return [
        {
          id: '1',
          location: 'productHeader',
          order: 0,
          fieldName: 'manufacturerName',
          name: equipmentFieldNameDictionary.manufacturerName,
          fieldFilter: equipmentFieldSortDictionary.manufacturerName,
        },
        {
          id: '2',
          location: 'productHeader',
          order: 1,
          fieldName: 'productModel',
          name: equipmentFieldNameDictionary.productModel,
          fieldFilter: equipmentFieldSortDictionary.productModel,
        },
        {
          id: '3',
          location: 'productHeader',
          order: 2,
          fieldName: 'productPage',
          name: equipmentFieldNameDictionary.productPage,
          fieldFilter: equipmentFieldSortDictionary.productPage,
        },
        {
          id: '4',
          location: 'productHeader',
          order: 3,
          fieldName: 'serial',
          name: equipmentFieldNameDictionary.serial,
          fieldFilter: equipmentFieldSortDictionary.serial,
        },
        {
          id: '5',
          location: 'productHeader',
          order: 4,
          fieldName: 'purchasedDate',
          name: equipmentFieldNameDictionary.purchasedDate,
          fieldFilter: equipmentFieldSortDictionary.purchasedDate,
        },
        {
          id: '6',
          location: 'productHeader',
          order: 5,
          fieldName: 'userName',
          name: equipmentFieldNameDictionary.userName,
          fieldFilter: equipmentFieldSortDictionary.userName,
        },
      ];
    if (location === 'softwareTable')
      return [
        {
          id: '0',
          location: 'softwareTable',
          order: 0,
          fieldName: 'key',
          name: softwareFieldNameDictionary.key,
          fieldFilter: softwareFieldSortDictionary.key,
        },
        {
          id: '1',
          location: 'softwareTable',
          order: 1,
          fieldName: 'vendor',
          name: softwareFieldNameDictionary.vendor,
          fieldFilter: softwareFieldSortDictionary.vendor,
        },
        {
          id: '2',
          location: 'softwareTable',
          order: 2,
          fieldName: 'purchasedDate',
          name: softwareFieldNameDictionary.purchasedDate,
          fieldFilter: softwareFieldSortDictionary.purchasedDate,
        },

        {
          id: '3',
          location: 'softwareTable',
          order: 3,
          fieldName: 'version',
          name: softwareFieldNameDictionary.version,
          fieldFilter: softwareFieldSortDictionary.version,
        },
      ];
    if (location === 'miscellaneousTable')
      return [
        {
          id: '0',
          location: 'miscellaneousTable',
          order: 0,
          fieldName: 'name',
          name: miscellaneousFieldNameDictionary.name,
          fieldFilter: miscellaneousFieldSortDictionary.name,
        },
        {
          id: '1',
          location: 'miscellaneousTable',
          order: 1,
          fieldName: 'purchasedDate',
          name: miscellaneousFieldNameDictionary.purchasedDate,
          fieldFilter: miscellaneousFieldSortDictionary.purchasedDate,
        },
        {
          id: '2',
          location: 'miscellaneousTable',
          order: 2,
          fieldName: 'vendor',
          name: miscellaneousFieldNameDictionary.vendor,
          fieldFilter: miscellaneousFieldSortDictionary.vendor,
        },

        {
          id: '3',
          location: 'miscellaneousTable',
          order: 3,
          fieldName: 'description',
          name: miscellaneousFieldNameDictionary.description,
          fieldFilter: miscellaneousFieldSortDictionary.description,
        },
      ];
    if (location === 'studioServiceTable')
      return [
        {
          id: '0',
          location: 'studioServiceTable',
          order: 0,
          fieldName: 'name',
          name: studioServiceFieldNameDictionary.name,
          fieldFilter: studioServiceFieldSortDictionary.name,
        },
        {
          id: '1',
          location: 'studioServiceTable',
          order: 1,
          fieldName: 'description',
          name: studioServiceFieldNameDictionary.description,
          fieldFilter: studioServiceFieldSortDictionary.description,
        },
        {
          id: '2',
          location: 'studioServiceTable',
          order: 2,
          fieldName: 'vendor',
          name: studioServiceFieldNameDictionary.vendor,
          fieldFilter: studioServiceFieldSortDictionary.vendor,
        },

        {
          id: '3',
          location: 'studioServiceTable',
          order: 3,
          fieldName: 'purchasedDate',
          name: studioServiceFieldNameDictionary.purchasedDate,
          fieldFilter: studioServiceFieldSortDictionary.purchasedDate,
        },
      ];
    if (location === 'softwareHeader')
      return [
        {
          id: '0',
          location: 'softwareHeader',
          order: 0,
          fieldName: 'key',
          name: softwareFieldNameDictionary.key,
          fieldFilter: softwareFieldSortDictionary.key,
        },
        {
          id: '1',
          location: 'softwareHeader',
          order: 1,
          fieldName: 'vendor',
          name: softwareFieldNameDictionary.vendor,
          fieldFilter: softwareFieldSortDictionary.vendor,
        },
        {
          id: '2',
          location: 'softwareHeader',
          order: 2,
          fieldName: 'purchasedDate',
          name: softwareFieldNameDictionary.purchasedDate,
          fieldFilter: softwareFieldSortDictionary.purchasedDate,
        },

        {
          id: '3',
          location: 'softwareHeader',
          order: 3,
          fieldName: 'version',
          name: softwareFieldNameDictionary.version,
          fieldFilter: softwareFieldSortDictionary.version,
        },
      ];
    if (location === 'miscellaneousHeader')
      return [
        {
          id: '0',
          location: 'miscellaneousHeader',
          order: 0,
          fieldName: 'name',
          name: miscellaneousFieldNameDictionary.name,
          fieldFilter: miscellaneousFieldSortDictionary.name,
        },
        {
          id: '1',
          location: 'miscellaneousHeader',
          order: 1,
          fieldName: 'purchasedDate',
          name: miscellaneousFieldNameDictionary.purchasedDate,
          fieldFilter: miscellaneousFieldSortDictionary.purchasedDate,
        },
        {
          id: '2',
          location: 'miscellaneousHeader',
          order: 2,
          fieldName: 'vendor',
          name: miscellaneousFieldNameDictionary.vendor,
          fieldFilter: miscellaneousFieldSortDictionary.vendor,
        },

        {
          id: '3',
          location: 'miscellaneousHeader',
          order: 3,
          fieldName: 'description',
          name: miscellaneousFieldNameDictionary.description,
          fieldFilter: miscellaneousFieldSortDictionary.description,
        },
      ];
    if (location === 'studioServiceHeader')
      return [
        {
          id: '0',
          location: 'studioServiceHeader',
          order: 0,
          fieldName: 'name',
          name: studioServiceFieldNameDictionary.name,
          fieldFilter: studioServiceFieldSortDictionary.name,
        },
        {
          id: '1',
          location: 'studioServiceHeader',
          order: 1,
          fieldName: 'description',
          name: studioServiceFieldNameDictionary.description,
          fieldFilter: studioServiceFieldSortDictionary.description,
        },
        {
          id: '2',
          location: 'studioServiceHeader',
          order: 2,
          fieldName: 'vendor',
          name: studioServiceFieldNameDictionary.vendor,
          fieldFilter: studioServiceFieldSortDictionary.vendor,
        },

        {
          id: '3',
          location: 'studioServiceHeader',
          order: 3,
          fieldName: 'purchasedDate',
          name: studioServiceFieldNameDictionary.purchasedDate,
          fieldFilter: studioServiceFieldSortDictionary.purchasedDate,
        },
      ];
    if (location === 'productTagTable')
      return [
        {
          id: '0',
          location: 'productTagTable',
          order: 0,
          fieldName: 'manufacturerName',
          name: equipmentFieldNameDictionary.manufacturerName,
          fieldFilter: equipmentFieldSortDictionary.manufacturerName,
        },
        {
          id: '1',
          location: 'productTagTable',
          order: 1,
          fieldName: 'productModel',
          name: equipmentFieldNameDictionary.productModel,
          fieldFilter: equipmentFieldSortDictionary.productModel,
        },
        {
          id: '2',
          location: 'productTagTable',
          order: 2,
          fieldName: 'serial',
          name: equipmentFieldNameDictionary.serial,
          fieldFilter: equipmentFieldSortDictionary.serial,
        },
        {
          id: '3',
          location: 'productTagTable',
          order: 3,
          fieldName: 'equipmentStatusName',
          name: equipmentFieldNameDictionary.equipmentStatusName,
          fieldFilter: equipmentFieldSortDictionary.equipmentStatusName,
        },
      ];
    if (location === 'serviceTagTable')
      return [
        {
          id: '0',
          location: 'serviceTagTable',
          order: 0,
          fieldName: 'vendor',
          name: studioServiceFieldNameDictionary.vendor,
          fieldFilter: studioServiceFieldSortDictionary.vendor,
        },
        {
          id: '1',
          location: 'serviceTagTable',
          order: 1,
          fieldName: 'name',
          name: studioServiceFieldNameDictionary.name,
          fieldFilter: studioServiceFieldSortDictionary.name,
        },
        {
          id: '2',
          location: 'serviceTagTable',
          order: 2,
          fieldName: 'description',
          name: studioServiceFieldNameDictionary.description,
          fieldFilter: studioServiceFieldSortDictionary.description,
        },
        {
          id: '3',
          location: 'serviceTagTable',
          order: 3,
          fieldName: 'purchasedDate',
          name: studioServiceFieldNameDictionary.purchasedDate,
          fieldFilter: studioServiceFieldSortDictionary.purchasedDate,
        },
      ];
    if (location === 'miscellaneousTagTable')
      return [
        {
          id: '0',
          location: 'miscellaneousTagTable',
          order: 0,
          fieldName: 'vendor',
          name: miscellaneousFieldNameDictionary.vendor,
          fieldFilter: miscellaneousFieldSortDictionary.vendor,
        },
        {
          id: '1',
          location: 'miscellaneousTagTable',
          order: 1,
          fieldName: 'name',
          name: miscellaneousFieldNameDictionary.name,
          fieldFilter: miscellaneousFieldSortDictionary.name,
        },
        {
          id: '2',
          location: 'miscellaneousTagTable',
          order: 2,
          fieldName: 'description',
          name: miscellaneousFieldNameDictionary.description,
          fieldFilter: miscellaneousFieldSortDictionary.description,
        },
        {
          id: '3',
          location: 'miscellaneousTagTable',
          order: 3,
          fieldName: 'purchasedDate',
          name: miscellaneousFieldNameDictionary.purchasedDate,
          fieldFilter: miscellaneousFieldSortDictionary.purchasedDate,
        },
      ];
    if (location === 'softwareTagTable')
      return [
        {
          id: '0',
          location: 'softwareTagTable',
          order: 0,
          fieldName: 'vendor',
          name: softwareFieldNameDictionary.vendor,
          fieldFilter: softwareFieldSortDictionary.vendor,
        },
        {
          id: '1',
          location: 'softwareTagTable',
          order: 1,
          fieldName: 'name',
          name: softwareFieldNameDictionary.name,
          fieldFilter: softwareFieldSortDictionary.name,
        },
        {
          id: '2',
          location: 'softwareTagTable',
          order: 2,
          fieldName: 'description',
          name: softwareFieldNameDictionary.description,
          fieldFilter: softwareFieldSortDictionary.description,
        },
        {
          id: '3',
          location: 'softwareTagTable',
          order: 3,
          fieldName: 'purchasedDate',
          name: softwareFieldNameDictionary.purchasedDate,
          fieldFilter: softwareFieldSortDictionary.purchasedDate,
        },
      ];
    return [];
  }
}
